// 🔁 Enhanced Global chunk load error handler
const RELOAD_KEY = "chunkReloadAttempt";
const MAX_RELOAD_ATTEMPTS = 3;

// Function to handle chunk load errors
const handleChunkLoadError = (errorMessage, errorSource = "unknown") => {
  console.warn(`Chunk load error detected from ${errorSource}:`, errorMessage);

  // // Get current reload attempt count
  const reloadAttempts = parseInt(sessionStorage.getItem(RELOAD_KEY) || "0");

  if (reloadAttempts < MAX_RELOAD_ATTEMPTS) {
    console.warn(
      `Reloading app... (Attempt ${reloadAttempts + 1}/${MAX_RELOAD_ATTEMPTS})`,
    );
    sessionStorage.setItem(RELOAD_KEY, String(reloadAttempts + 1));

    // Add a small delay before reload to prevent rapid reloading
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  } else {
    console.error("Maximum reload attempts reached. Manual refresh required.");
    sessionStorage.removeItem(RELOAD_KEY);

    // Optionally show user notification
    if (
      window.confirm(
        "Application failed to load properly. Would you like to clear cache and reload?",
      )
    ) {
      // Clear all storage and reload
      sessionStorage.clear();
      localStorage.clear();
      window.location.reload(true); // Force reload from server
    }
  }
};

// Enhanced error event listener
window.addEventListener("error", (event) => {
  const error = event?.error || event;
  const errorMessage = error?.message || "";

  // Check for chunk load errors
  if (errorMessage && /Loading (CSS )?chunk \d+ failed/i.test(errorMessage)) {
    handleChunkLoadError(errorMessage, "error event");
    return;
  }

  // Check for MIME type errors (your specific case)
  if (errorMessage && /MIME type.*not executable/i.test(errorMessage)) {
    handleChunkLoadError(errorMessage, "MIME type error");
    return;
  }

  // Check for script loading errors
  if (event.target && event.target.tagName === "SCRIPT") {
    const scriptSrc = event.target.src || "";
    if (scriptSrc.includes(".js")) {
      handleChunkLoadError(
        `Script loading failed: ${scriptSrc}`,
        "script error",
      );
      return;
    }
  }
});

// Enhanced unhandled rejection listener
window.addEventListener("unhandledrejection", (event) => {
  const error = event?.reason;
  const errorMessage = error?.message || error?.toString() || "";

  // Check for chunk load errors
  if (errorMessage && /Loading (CSS )?chunk \d+ failed/i.test(errorMessage)) {
    handleChunkLoadError(errorMessage, "unhandled rejection");
    event.preventDefault(); // Prevent the error from being logged to console
    return;
  }

  // Check for ChunkLoadError specifically
  if (error && error.name === "ChunkLoadError") {
    handleChunkLoadError(errorMessage, "ChunkLoadError");
    event.preventDefault();
    return;
  }

  // Check for import/dynamic import failures
  if (errorMessage && /import.*failed/i.test(errorMessage)) {
    handleChunkLoadError(errorMessage, "import failure");
    event.preventDefault();
    return;
  }
});

// Clear reload attempts on successful load
window.addEventListener("load", () => {
  sessionStorage.removeItem(RELOAD_KEY);
});

// Vue/Webpack specific chunk error handling
const originalError = console.error;
console.error = function (...args) {
  const message = args.join(" ");
  if (message && /chunk.*failed/i.test(message)) {
    handleChunkLoadError(message, "console error");
  }
  originalError.apply(console, args);
};

import { createApp } from "vue";
import App from "./App.vue";
import { createPinia } from "pinia";

/** Bootstrap  */
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";

/** Router */
import router from "./router/router";
import menuItems from "./router/router";

/** Charts */
import VueApexCharts from "vue3-apexcharts";

/** Pinia (Vue State Management) */

/** Axios */
import axios from "axios";

/** PrimeVue */
import PrimeVue from "primevue/config";
import "primevue/resources/themes/lara-light-indigo/theme.css"; //theme
import "primevue/resources/primevue.min.css"; //core CSS
import Tooltip from "primevue/tooltip";

/** Data Tables */
import Vue3Datatable from "@bhplugin/vue3-datatable";

import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";

/** Remixicon */
import "../src/assets/remixicon/fonts/remixicon.css";

/** Toast */
import ToastPlugin from "vue-toast-notification";

import CKEditor from "@ckeditor/ckeditor5-vue";

import { useAuthStore } from "./store/auth";
import { isLoading } from "./store/LoadingState";

import VueSSE from "vue-sse";

window.addEventListener("storage", (event) => {
  if (event.key === "auth.currentUser" && event.newValue === null) {
    const authStore = useAuthStore();
    authStore.clearAuthState(); // force logout in this tab
  }
});

// Enhanced router error handling
router.onError((error) => {
  console.error("Router error:", error);
  if (error.message && /Loading (CSS )?chunk \d+ failed/i.test(error.message)) {
    handleChunkLoadError(error.message, "router error");
  }
});

const app = createApp(App);

// Add global error handler for Vue app
app.config.errorHandler = (err, instance, info) => {
  console.error("Vue error:", err, info);
  if (
    err &&
    err.message &&
    /Loading (CSS )?chunk \d+ failed/i.test(err.message)
  ) {
    handleChunkLoadError(err.message, "vue error");
  }
};

app.use(createPinia());
app.component("VueDatePicker", VueDatePicker);
app.component(Vue3Datatable);
app.use(router, menuItems);
app.use(CKEditor);
app.use(PrimeVue);

// app.use(VueSSE, {
//   format: "json",
//   polyfill: true,
//   url: "/my-events-server",
//   withCredentials: true,
// });

// OR specify custom defaults (described below)
app.use(VueSSE, {
  format: "json",
  polyfill: true,
  url: "/my-events-server",
  withCredentials: true,
});
app.directive("tooltip", Tooltip);
app.use(VueApexCharts);
app.use(ToastPlugin);
app.mount("#app");

let baseURL = "https://app-qa.rdep.io";

if (
  process.env.NODE_ENV !== "development" &&
  process.env.VUE_APP_BASE_URL !== ""
) {
  baseURL = process.env.VUE_APP_BASE_URL;
}
axios.defaults.baseURL = baseURL;
app.config.globalProperties.$http = axios;

// Create an Axios interceptor
let isLoggingOut = false;

// Request interceptor
axios.interceptors.request.use(
  (config) => {
    isLoading.value += 1;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor
axios.interceptors.response.use(
  (response) => {
    if (response) {
      if (isLoading.value > 0) {
        isLoading.value -= 1;
      }
    }
    return response;
  },
  (error) => {
    if (error.response || axios.isCancel(error)) {
      if (isLoading.value > 0) {
        isLoading.value -= 1;
      }

      // if error .response in null or not check
      if (!error.response) {
        return Promise.reject(error);
      }
      if (error.response.status === 401 && !isLoggingOut) {
        isLoggingOut = true;
        const authStore = useAuthStore();
        authStore.clearAuthState();
        isLoggingOut = false;
      }
    }
    return Promise.reject(error);
  },
);
